<template>
  <v-dialog v-model="isOpen" max-width="800" class="precedent-dialog-blur">
    <v-card class="precedent-modal">
      <v-card-title class="precedent-modal-header">
        <span class="material-symbols-outlined">search</span>
        <span>Emsal Karar Ara</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close" size="small">
          <span class="material-symbols-outlined">close</span>
        </v-btn>
      </v-card-title>

      <v-card-text class="precedent-modal-body">
        <!-- Arama Formu -->
        <div class="search-form">
          <v-text-field
            v-model="searchQuery"
            label="Arama sorgusu"
            placeholder="Örn: trafik kazası tazminat, kira sözleşmesi feshi..."
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="loading"
            @keyup.enter="search"
            autofocus
          >
            <template #append-inner>
              <v-btn
                color="primary"
                variant="flat"
                :loading="loading"
                :disabled="!searchQuery || searchQuery.length < 3"
                @click="search"
                size="small"
              >
                <span class="material-symbols-outlined">search</span>
                Ara
              </v-btn>
            </template>
          </v-text-field>
        </div>

        <!-- Hata Mesajı -->
        <v-alert v-if="error" type="error" variant="tonal" class="mt-4" closable @click:close="error = ''">
          {{ error }}
        </v-alert>

        <!-- Yükleniyor -->
        <div v-if="loading" class="loading-state">
          <v-progress-circular indeterminate color="primary" />
          <span>Emsal kararlar aranıyor...</span>
        </div>

        <!-- Sonuçlar -->
        <div v-else-if="hasSearched" class="results-container">
          <!-- Özet -->
          <div v-if="summary" class="summary-section">
            <h4>
              <span class="material-symbols-outlined">summarize</span>
              Özet
            </h4>
            <div class="summary-content" v-html="summary"></div>
          </div>

          <!-- Kaynak Dosyalar -->
          <div v-if="results.length > 0" class="sources-section">
            <h4>
              <span class="material-symbols-outlined">folder_open</span>
              Kaynak Belgeler ({{ results.length }})
            </h4>
            <div class="source-cards">
              <div
                v-for="(result, index) in results"
                :key="index"
                class="source-card"
                @click="selectResult(result)"
              >
                <div class="source-index">
                  <span class="index-number">[{{ result.index || index + 1 }}]</span>
                </div>
                <div class="source-info">
                  <div class="source-name">{{ result.file_name }}</div>
                  <div v-if="result.quote" class="source-quote">
                    "{{ truncateText(result.quote, 150) }}"
                  </div>
                </div>
                <div class="source-action">
                  <v-btn
                    variant="tonal"
                    color="primary"
                    size="small"
                    @click.stop="addToChat(result)"
                  >
                    <span class="material-symbols-outlined">add_comment</span>
                    Sohbete Ekle
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    color="primary"
                    size="small"
                    @click.stop="downloadFile(result)"
                    :loading="downloadingFile === result.file_name"
                  >
                    <span class="material-symbols-outlined">download</span>
                    İndir
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Sonuç yok -->
          <div v-else-if="!summary" class="no-results">
            <span class="material-symbols-outlined">search_off</span>
            <p>Aramanızla eşleşen emsal karar bulunamadı.</p>
          </div>
        </div>

        <!-- Başlangıç Durumu -->
        <div v-else class="initial-state">
          <span class="material-symbols-outlined">gavel</span>
          <p>Emsal karar aramak için yukarıdaki alana anahtar kelimeler girin.</p>
          <small>Örnek: "trafik kazası tazminat", "iş sözleşmesi feshi", "kira artış oranı"</small>
        </div>
      </v-card-text>

      <v-card-actions class="precedent-modal-footer">
        <v-btn variant="text" @click="close">Kapat</v-btn>
        <v-spacer />
        <v-btn
          v-if="summary"
          variant="tonal"
          color="primary"
          @click="addSummaryToChat"
        >
          <span class="material-symbols-outlined">add_comment</span>
          Özeti Sohbete Ekle
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Döküman Önizleme Modal -->
    <DocumentPreviewModal
      v-model="previewVisible"
      :document="selectedDocument"
      @add-to-chat="handlePreviewAddToChat"
    />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import axios from "axios";
import { useToast } from "vue-toastification";
import DocumentPreviewModal from "./DocumentPreviewModal.vue";

const toast = useToast();

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

interface PrecedentResult {
  file_id?: string;
  file_name: string;
  quote?: string;
  index?: number;
}

export default defineComponent({
  name: "PrecedentSearchModal",

  components: {
    DocumentPreviewModal,
  },

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "add-to-chat"],

  setup(props, { emit }) {
    const searchQuery = ref("");
    const loading = ref(false);
    const error = ref("");
    const summary = ref("");
    const rawText = ref("");
    const results = ref<PrecedentResult[]>([]);
    const hasSearched = ref(false);

    // Önizleme modal state
    const previewVisible = ref(false);
    const selectedDocument = ref<PrecedentResult | null>(null);

    // İndirme state
    const downloadingFile = ref<string | null>(null);

    const isOpen = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    const authHeaders = () => {
      const token = localStorage.getItem("auth_token");
      return {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
      };
    };

    const search = async () => {
      if (!searchQuery.value || searchQuery.value.length < 3) {
        error.value = "Arama sorgusu en az 3 karakter olmalıdır.";
        return;
      }

      loading.value = true;
      error.value = "";
      summary.value = "";
      rawText.value = "";
      results.value = [];

      try {
        const res = await axios.post(
          `${API_BASE_URL}/search/precedents`,
          { query: searchQuery.value },
          { headers: authHeaders() }
        );

        if (res.data.ok) {
          summary.value = res.data.summary || "";
          rawText.value = res.data.raw_text || "";
          results.value = res.data.results || [];
        } else {
          error.value = res.data.error || "Arama başarısız oldu.";
        }
      } catch (err: any) {
        error.value =
          err?.response?.data?.error ||
          err?.message ||
          "Arama sırasında bir hata oluştu.";
      } finally {
        loading.value = false;
        hasSearched.value = true;
      }
    };

    const truncateText = (text: string, maxLength: number) => {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    };

    const selectResult = (result: PrecedentResult) => {
      selectedDocument.value = result;
      previewVisible.value = true;
    };

    const handlePreviewAddToChat = (text: string) => {
      emit("add-to-chat", text);
      previewVisible.value = false;
      close();
    };

    const addToChat = (result: PrecedentResult) => {
      const text = `[Emsal Karar: ${result.file_name}]\n${result.quote || ""}`;
      emit("add-to-chat", text);
      close();
    };

    const downloadFile = async (result: PrecedentResult) => {
      if (!result.file_name) {
        error.value = "Dosya adı bulunamadı.";
        return;
      }

      downloadingFile.value = result.file_name;

      try {
        const fileId = result.file_id || "local";
        const response = await axios.get(
          `${API_BASE_URL}/files/${fileId}/download`,
          {
            headers: authHeaders(),
            params: { filename: result.file_name },
            responseType: "blob",
          }
        );

        // Blob'dan dosya indir
        const blob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/octet-stream",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = result.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        console.error("[PrecedentSearch] Download error:", err);

        // 404 hatası - dosya bulunamadı
        if (err?.response?.status === 404) {
          toast.warning("Dosya bulunamadı. Lütfen mevzuat.mahkemeler.net adresinden kontrol ediniz.", {
            timeout: 6000,
          });
        } else {
          toast.error(
            err?.response?.data?.error ||
            err?.message ||
            "Dosya indirilirken bir hata oluştu."
          );
        }
      } finally {
        downloadingFile.value = null;
      }
    };

    const addSummaryToChat = () => {
      emit("add-to-chat", rawText.value);
      close();
    };

    const close = () => {
      isOpen.value = false;
    };

    const reset = () => {
      searchQuery.value = "";
      loading.value = false;
      error.value = "";
      summary.value = "";
      rawText.value = "";
      results.value = [];
      hasSearched.value = false;
    };

    // Modal açıldığında önceki aramayı sıfırla
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          reset();
        }
      }
    );

    return {
      isOpen,
      searchQuery,
      loading,
      error,
      summary,
      results,
      hasSearched,
      previewVisible,
      selectedDocument,
      downloadingFile,
      search,
      truncateText,
      selectResult,
      handlePreviewAddToChat,
      addToChat,
      addSummaryToChat,
      downloadFile,
      close,
      reset,
    };
  },
});
</script>

<style lang="scss" scoped>
.precedent-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.precedent-modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #5a6270 0%, #6b7280 100%);
  color: white;

  .material-symbols-outlined {
    font-size: 24px;
  }
}

.precedent-modal-body {
  padding: 24px !important;
  min-height: 400px;
  max-height: 60vh;
  overflow-y: auto;
}

.search-form {
  margin-bottom: 16px;

  .v-btn {
    margin-left: 8px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: #666;
}

.initial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #888;

  .material-symbols-outlined {
    font-size: 64px;
    color: #ccc;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    margin-bottom: 8px;
  }

  small {
    color: #aaa;
  }
}

.results-container {
  .summary-section,
  .sources-section {
    margin-bottom: 24px;

    h4 {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e0e0e0;

      .material-symbols-outlined {
        font-size: 20px;
        color: #5a6270;
      }
    }
  }

  .summary-content {
    background: #f8faf9;
    border-radius: 12px;
    padding: 16px 20px;
    font-size: 14px;
    line-height: 1.7;
    color: #444;
    border-left: 4px solid #5a6270;
  }

  .source-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .source-card {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #5a6270;
      box-shadow: 0 2px 8px rgba(90, 98, 112, 0.1);
    }

    .source-index {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #5a6270 0%, #6b7280 100%);
      border-radius: 10px;

      .index-number {
        font-size: 16px;
        font-weight: 700;
        color: #fff;
      }
    }

    .source-info {
      flex: 1;
      min-width: 0;

      .source-name {
        font-weight: 600;
        font-size: 14px;
        color: #333;
        margin-bottom: 4px;
      }

      .source-quote {
        font-size: 13px;
        color: #666;
        font-style: italic;
        line-height: 1.5;
      }
    }

    .source-action {
      flex-shrink: 0;
      display: flex;
      gap: 8px;

      .v-btn {
        .material-symbols-outlined {
          font-size: 18px;
          margin-right: 4px;
        }
      }
    }
  }
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #888;

  .material-symbols-outlined {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 12px;
  }
}

.precedent-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;
}

@media (max-width: 600px) {
  .source-card {
    flex-direction: column;

    .source-action {
      width: 100%;
      margin-top: 12px;
      flex-direction: column;

      .v-btn {
        width: 100%;
      }
    }
  }
}
</style>

<style lang="scss">
/* Dialog blur overlay - global scope gerekli çünkü dialog teleport ediliyor */
.precedent-dialog-blur {
  .v-overlay__scrim {
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    background: rgba(0, 0, 0, 0.5) !important;
  }
}
</style>
